// useVarietyFamilies — fetches the data-driven variety fold rules
// (variety_family_rules, mig 1039) and returns bound varietyFamily/varietyMatches
// that apply them. Falls back to the hardcoded lemon rule when the table is empty
// or unreachable (offline / cold cache), so matching is unchanged in those cases.
//
// Why a hook: the fold rules now live in the DB so a new family is a data edit,
// not a code change. The pure functions in varietyFamily.ts take the rules as an
// argument (and default to the lemon fallback) so they stay unit-testable.
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  varietyFamily, varietyMatches, fruitTypeMatchesRun,
  DEFAULT_VARIETY_FAMILY_RULES, type VarietyFamilyRule,
} from '@/lib/varietyFamily'

export function useVarietyFamilies() {
  const { data: rules = [] } = useQuery({
    queryKey: ['variety-family-rules'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('variety_family_rules')
        .select('token, family')
        .eq('is_active', true)
        .order('sort_order')
      if (error) throw error
      return (data ?? []) as VarietyFamilyRule[]
    },
  })

  return useMemo(() => {
    // Empty / offline → the hardcoded lemon fallback (unchanged behaviour).
    const eff = rules.length ? rules : DEFAULT_VARIETY_FAMILY_RULES
    return {
      varietyFamily:  (v: string | null | undefined) => varietyFamily(v, eff),
      varietyMatches: (fruitType: string | null | undefined, runVariety: string | null | undefined) =>
        varietyMatches(fruitType, runVariety, eff),
      fruitTypeMatchesRun: (fruitType: string | null | undefined, runVariety: string | null | undefined, runCommodity: string | null | undefined) =>
        fruitTypeMatchesRun(fruitType, runVariety, runCommodity, eff),
      // The effective rule set itself — for callers that hand rules to pure
      // batch evaluators (setupHealth) instead of matching one pair at a time.
      rules: eff,
    }
  }, [rules])
}
